import { Checkbox } from '@bbpos/ui';
import type { UserLocation } from '@/types/user';

interface LocationAssignmentProps {
  locations: UserLocation[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  error?: string;
}

export function LocationAssignment({
  locations,
  selected,
  onChange,
  disabled,
  error,
}: LocationAssignmentProps) {
  const allSelected = locations.length > 0 && selected.length === locations.length;
  const someSelected = selected.length > 0 && selected.length < locations.length;

  function toggleAll() {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(locations.map((l) => l.id));
    }
  }

  function toggleOne(id: string) {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  }

  return (
    <div aria-label="Select locations to assign">
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_4px_16px_-8px_rgba(15,23,42,0.12)] divide-y divide-slate-100">
        {/* Select All */}
        <label className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleAll}
            disabled={disabled}
          />
          <span className="text-sm font-semibold text-slate-700">
            Select All ({locations.length})
          </span>
        </label>

        {/* Individual locations */}
        {locations.map((loc) => {
          const isChecked = selected.includes(loc.id);
          return (
            <label
              key={loc.id}
              className={[
                'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
                isChecked ? 'bg-indigo-50/40' : 'hover:bg-slate-50',
              ].join(' ')}
            >
              <Checkbox
                checked={isChecked}
                onChange={() => toggleOne(loc.id)}
                disabled={disabled}
              />
              <div className="flex items-center gap-2">
                <div
                  className={[
                    'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold',
                    isChecked
                      ? 'bg-indigo-100 text-indigo-600'
                      : 'bg-slate-100 text-slate-400',
                  ].join(' ')}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700">{loc.name}</span>
                  {loc.code && (
                    <span className="ml-2 text-xs text-slate-400 font-mono">{loc.code}</span>
                  )}
                </div>
              </div>
            </label>
          );
        })}

        {locations.length === 0 && (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">No locations available</p>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-rose-600">{error}</p>}
      <p className="mt-1.5 text-xs text-slate-400">
        User will only see data from assigned locations
      </p>
    </div>
  );
}
